import React, { useState, useEffect, useRef } from "react";
import ApiClient from "../api/ApiClient";
import { createChart } from "lightweight-charts";

// Map signal action to a display colour name
const ACTION_COLORS = {
  Buy: { up: "#22c55e", down: "#16a34a", wick: "#15803d" },
  Sell: { up: "#ef4444", down: "#b91c1c", wick: "#991b1b" },
  Money: { up: "#94a3b8", down: "#64748b", wick: "#475569" },
};

const DEFAULT_COLORS = { up: "#4caf50", down: "#f44336", wick: "#f44336" };

export const SignalsOverlay = () => {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayData, setOverlayData] = useState(null); // { Ticker, Signals, ModelName }
  const [candles, setCandles] = useState([]);
  const [showArrows, setShowArrows] = useState(true);
  const [colorCodeCandles, setColorCodeCandles] = useState(true);

  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const markersRef = useRef([]);
  const overlayDataRef = useRef(null);  // mirrors overlayData for use inside chart callbacks
  const candlesRef = useRef([]);        // mirrors candles for use inside chart callbacks
  const fetchingMoreRef = useRef(false);
  const noMoreDataRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // Keep refs in sync with state so chart callbacks always see fresh values
  useEffect(() => { overlayDataRef.current = overlayData; }, [overlayData]);
  useEffect(() => { candlesRef.current = candles; }, [candles]);

  // Load models list on mount
  useEffect(() => {
    ApiClient.getModelsList()
      .then((list) => {
        setModels(list || []);
        if (list && list.length > 0) {
          setSelectedModelId(String(list[0].Id));
        }
      })
      .catch((err) => console.error("Failed to load models", err));
  }, []);

  // Initialise lightweight-charts on mount
  useEffect(() => {
    const container = document.getElementById("signalsOverlayChartContainer");
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 500,
      layout: { background: { color: "#ffffff" }, textColor: "#000" },
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { timeVisible: false, secondsVisible: false },
    });
    chartRef.current = chart;

    const candleSeries = chart.addCandlestickSeries({
      upColor: DEFAULT_COLORS.up,
      downColor: DEFAULT_COLORS.down,
      borderDownColor: DEFAULT_COLORS.down,
      borderUpColor: DEFAULT_COLORS.up,
      wickDownColor: DEFAULT_COLORS.down,
      wickUpColor: DEFAULT_COLORS.up,
    });
    candleSeriesRef.current = candleSeries;

    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.applyOptions({ width: container.clientWidth });
      }
    };
    window.addEventListener("resize", handleResize);

    // Subscribe to visible range changes to load earlier candles on left-scroll
    const unsubscribeRange = chart.timeScale().subscribeVisibleTimeRangeChange(async (range) => {
      try {
        if (!range) return;
        const currentCandles = candlesRef.current;
        if (!currentCandles || currentCandles.length === 0) return;
        if (fetchingMoreRef.current || noMoreDataRef.current) return;

        const fromSec = typeof range.from === "number"
          ? range.from
          : Math.floor(new Date(range.from).getTime() / 1000);
        const toSec = typeof range.to === "number"
          ? range.to
          : Math.floor(new Date(range.to).getTime() / 1000);
        const visibleSpan = Math.max(1, toSec - fromSec);
        const threshold = Math.floor(visibleSpan * 0.25);

        // Find the unix-second timestamp of the earliest loaded candle
        const earliest = currentCandles.reduce((min, c) => {
          const t = Math.floor(new Date(c.Time).getTime() / 1000);
          return t < min ? t : min;
        }, Infinity);

        if (fromSec < earliest + threshold) {
          fetchingMoreRef.current = true;
          try {
            const ticker = overlayDataRef.current?.Ticker;
            if (!ticker?.Id) return;

            // Fetch the next batch ending just before the earliest candle we have
            const toDate = new Date(earliest * 1000);
            const fromDate = new Date(toDate.getTime() - 5000 * 86400 * 1000); // ~5000 days back

            const res = await ApiClient.searchCandles({
              TickerId: ticker.Id,
              Interval: "Day",
              Aggregated: false,
              TimeTo: toDate.toISOString(),
              TimeFrom: fromDate.toISOString(),
              Limit: 5000,
              Offset: 0,
            });

            const newItems = res.Items || [];
            if (newItems.length === 0) {
              noMoreDataRef.current = true;
              return;
            }

            // Merge with existing candles (deduplicate by Time string)
            setCandles((prev) => {
              const seen = new Set(prev.map((c) => c.Time));
              const merged = [
                ...newItems.filter((c) => !seen.has(c.Time)),
                ...prev,
              ];
              merged.sort((a, b) => new Date(a.Time) - new Date(b.Time));
              return merged;
            });
          } finally {
            fetchingMoreRef.current = false;
          }
        }
      } catch (e) {
        console.error("Left-scroll candle load failed", e);
        fetchingMoreRef.current = false;
      }
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      if (unsubscribeRange) unsubscribeRange();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }
    };
  }, []);

  // Whenever overlayData or candles change, push candles to the chart
  useEffect(() => {
    if (!candleSeriesRef.current || !overlayData) return;

    const { Signals } = overlayData;

    // Sort signals ascending by time (should already be sorted from backend)
    const sortedSignals = [...(Signals || [])].sort(
      (a, b) => new Date(a.Time) - new Date(b.Time)
    );

    // Build chart data, optionally colour-coding candles by active signal segment
    const chartData = (candles || []).map((c) => {
      const candleDate = new Date(c.Time);
      const timeSec = Math.floor(candleDate.getTime() / 1000);

      let segmentColors = null;
      if (colorCodeCandles) {
        // Find the most recent signal at or before this candle
        for (let i = sortedSignals.length - 1; i >= 0; i--) {
          const sigTimeSec = Math.floor(new Date(sortedSignals[i].Time).getTime() / 1000);
          if (sigTimeSec <= timeSec) {
            segmentColors = ACTION_COLORS[sortedSignals[i].Action];
            break;
          }
        }
      }

      const entry = {
        time: timeSec,
        open: Number(c.Open),
        high: Number(c.High),
        low: Number(c.Low),
        close: Number(c.Close),
      };
      if (segmentColors) {
        entry.color = segmentColors.up;
        entry.borderColor = segmentColors.down;
        entry.wickColor = segmentColors.wick;
      }
      return entry;
    });

    // Sort by time ascending (required by lightweight-charts)
    chartData.sort((a, b) => a.time - b.time);

    try {
      candleSeriesRef.current.setData(chartData);
    } catch (err) {
      console.error("Chart setData failed", err);
    }

    // Add signal markers on the chart (only when showArrows is enabled)
    try {
      const markers = showArrows
        ? sortedSignals
            .flatMap((sig) => {
              const sigTimeSec = Math.floor(new Date(sig.Time).getTime() / 1000);
              // Find the closest candle at or after the signal date
              const match = chartData.find((c) => c.time >= sigTimeSec);
              if (!match) return [];

              const action = sig.Action;
              const position = action === "Buy" ? "belowBar" : "aboveBar";
              const shapeColor =
                action === "Buy"
                  ? "#116d33"
                  : action === "Sell"
                  ? "#ad2a2a"
                  : "#94a3b8";

              // Two markers per signal: colored shape (no text) + black label (no shape)
              return [
                {
                  time: match.time,
                  position,
                  color: shapeColor,
                  shape: action === "Buy" ? "arrowUp" : action === "Sell" ? "arrowDown" : "circle",
                  text: "",
                  size: 1,
                },
                {
                  time: match.time,
                  position,
                  color: "#000000",
                  shape: "circle",
                  text: action === "Buy" ? "𝗕𝘂𝘆" : action === "Sell" ? "𝗦𝗲𝗹𝗹" : action,
                  size: 0,
                },
              ];
            })
            .filter(Boolean)
        : [];

      markersRef.current = markers;
      candleSeriesRef.current.setMarkers(markers);
    } catch (err) {
      console.error("setMarkers failed", err);
    }

    // Fit chart to data only on the initial load; preserve position when appending earlier candles
    if (chartRef.current) {
      try {
        if (!initialLoadDoneRef.current) {
          chartRef.current.timeScale().fitContent();
          initialLoadDoneRef.current = true;
        }
      } catch (e) {
        // ignore
      }
    }
  }, [overlayData, candles, showArrows, colorCodeCandles]);

  async function loadOverlay() {
    if (!selectedModelId) {
      setError("Please select a model");
      return;
    }

    setLoading(true);
    setError(null);
    setOverlayData(null);
    setCandles([]);
    noMoreDataRef.current = false;
    fetchingMoreRef.current = false;
    initialLoadDoneRef.current = false;

    try {
      const data = await ApiClient.getSignalOverlay(parseInt(selectedModelId));
      if (data.Error) {
        setError(data.Error);
        return;
      }

      setOverlayData(data);

      // Fetch candles for the matched ticker via the existing candles API
      if (data.Ticker?.Id) {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

        const candleRes = await ApiClient.searchCandles({
          TickerId: data.Ticker.Id,
          Interval: "Day",
          Aggregated: false,
          TimeFrom: twoYearsAgo.toISOString(),
          Limit: 5000,
          Offset: 0,
        });
        setCandles(candleRes.Items || []);
      }
    } catch (err) {
      console.error("Failed to load signal overlay", err);
      setError(err.message || "Failed to load signal overlay");
    } finally {
      setLoading(false);
    }
  }

  function renderLegend() {
    if (!overlayData || !overlayData.Signals || overlayData.Signals.length === 0) {
      return null;
    }

    const sortedSignals = [...overlayData.Signals].sort(
      (a, b) => new Date(a.Time) - new Date(b.Time)
    );

    return (
      <div className="mt-3">
        <h6 className="fw-semibold">Signals ({sortedSignals.length})</h6>
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          <table className="table table-sm table-striped mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedSignals.map((sig, i) => {
                const colors = ACTION_COLORS[sig.Action] || DEFAULT_COLORS;
                const date = new Date(sig.Time);
                return (
                  <tr key={i}>
                    <td>{date.toISOString().slice(0, 10)}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: colors.up, color: "#fff" }}
                      >
                        {sig.Action}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1>Signals Overlay</h1>
      <p className="text-muted">
        Pick a model to view its candle chart with signal actions colour-coded.{" "}
        <strong style={{ color: ACTION_COLORS.Buy.up }}>Green</strong> = Buy,{" "}
        <strong style={{ color: ACTION_COLORS.Sell.up }}>Red</strong> = Sell,{" "}
        <strong style={{ color: ACTION_COLORS.Money.up }}>Grey</strong> = Money.
      </p>

      <div className="mb-2 d-flex align-items-center gap-4">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="showArrowsCheck"
            checked={showArrows}
            onChange={(e) => setShowArrows(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showArrowsCheck">
            Show arrows
          </label>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="colorCodeCandlesCheck"
            checked={colorCodeCandles}
            onChange={(e) => setColorCodeCandles(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="colorCodeCandlesCheck">
            Color-code candles
          </label>
        </div>
      </div>

      <div className="mb-3 d-flex align-items-end gap-3" style={{ maxWidth: 600 }}>
        <div className="flex-grow-1">
          <label className="form-label fw-semibold">Model</label>
          <select
            className="form-select"
            value={selectedModelId}
            onChange={(e) => setSelectedModelId(e.target.value)}
          >
            <option value="">— Select a model —</option>
            {models.map((m) => (
              <option key={m.Id} value={m.Id}>
                {m.Name || m.FileName || `Model #${m.Id}`}
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={loadOverlay}
          disabled={loading || !selectedModelId}
        >
          {loading ? "Loading…" : "Load"}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {overlayData && (
        <div className="mb-2">
          <span className="text-muted small">
            Ticker:{" "}
            <strong>
              {overlayData.Ticker?.Code || overlayData.Ticker?.Name || "N/A"}
            </strong>
            {overlayData.Ticker?.Name && overlayData.Ticker?.Code && (
              <> — {overlayData.Ticker.Name}</>
            )}
            {overlayData.Ticker?.Id && (
              <>
                {" "}(
                <a
                  href={`/candles/ticker/${overlayData.Ticker.Id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted"
                >
                  #{overlayData.Ticker.Id}
                </a>
                {" · "}
                <a
                  href={`/candles/ticker/${overlayData.Ticker.Id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Show candles
                </a>
                )
              </>
            )}
            &emsp;Candles: <strong>{candles.length}</strong>
            &emsp;Signals: <strong>{(overlayData.Signals || []).length}</strong>
          </span>
        </div>
      )}

      {/* Chart is always in DOM so the canvas isn't destroyed on re-renders */}
      <div style={{ position: "relative", height: 500 }}>
        <div id="signalsOverlayChartContainer" style={{ height: 500 }} />
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.65)",
              zIndex: 10,
            }}
          >
            <em>Loading…</em>
          </div>
        )}
        {!overlayData && !loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#888",
              pointerEvents: "none",
            }}
          >
            Select a model and click Load to display the chart.
          </div>
        )}
      </div>

      {/* Colour legend */}
      <div className="mt-3 d-flex gap-4 flex-wrap">
        {Object.entries(ACTION_COLORS).map(([action, colors]) => (
          <div key={action} className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 3,
                backgroundColor: colors.up,
              }}
            />
            <span className="small">{action}</span>
          </div>
        ))}
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              backgroundColor: DEFAULT_COLORS.up,
              border: "1px dashed #aaa",
            }}
          />
          <span className="small">No signal yet</span>
        </div>
      </div>

      {renderLegend()}
    </div>
  );
};

export default SignalsOverlay;
